import { useEffect, useRef, useState } from 'react'

import {
  GenerationSourceTypeForSetting,
  type StackOrderItem,
} from '@ebalance/proto/bffuser/v1/setting_model_pb'
import { useTranslation } from 'react-i18next'

import {
  Box,
  Flex,
  LightModeSemanticColors,
  SecondaryButton,
  TertiaryButton,
  Text,
  chakra,
} from '@enechain/ecloud-designsystem'
import {
  FaChevronDownRegular,
  FaChevronUpRegular,
} from '@enechain/ecloud-icons'

import { ButtonTooltip } from '~/components/common/ButtonTooltip'
import { ConfirmDialog } from '~/components/common/ConfirmDialog'
import {
  DEFAULT_COLORS,
  DEFAULT_STACK_ORDER_ITEMS,
  SOURCE_TYPE_I18N_KEYS,
} from '~/constants/generationSource'
import { iconSize } from '~/constants/iconSize'

type Props = {
  /**
   * 行リストの最大幅。既定は480px。
   * モーダルのように器の幅に合わせたい呼び出し元は `"100%"` を渡す
   * ([#9163] 発電同時同量のモーダルでは、右側に器との余白が空いて
   * ヘッダ行〈案内文 + リセット〉とリストの右端が揃わなかった)。
   */
  maxWidth?: string
  value: StackOrderItem[]
  onMoveUp: (index: number) => void
  onMoveDown: (index: number) => void
  onResetDefault: () => void
  onColorChange: (source: GenerationSourceTypeForSetting, color: string) => void
}

/**
 * 同時同量（発電）の積み上げ順 + 色設定リスト。
 * 発電同時同量画面の積み上げ順・色エディタ。
 *
 * インタラクション (design 2026-07-29 改訂に追随):
 * - **各行に ↑↓** (= `.ecl-reorder-actions` の `btn-tertiary btn-square btn-sm`)。
 *   端の行はその向きだけ無効。
 * - 上部は **リセットのみ**。行を選んでから toolbar の ▲▼ を押す旧方式は、
 *   「選択」という中間状態が要るぶん手数が多く、欠損補完設定
 *   (`BalanceImputationPolicy/GenerationMethodOrderList`) やケーステンプレートと
 *   操作が食い違っていた。design 側が 3 画面を行内 ↑↓ に揃えたのでこちらも合わせる。
 * - 色入力は color picker (= 28x24、 内部 padding ゼロ、 .gc-stack-color 相当)
 *
 * **[design 差異] ドラッグ&ドロップは持たない。** design は行の D&D も持つが、
 * ↑↓ だけでも並べ替えは完結する。案内文でも D&D に触れない (できないことを
 * 案内しない)。D&D は別 issue。
 */

/**
 * 並べ替え 1 ボタン。 design `.ecl-reorder-actions` の
 * `btn-tertiary btn-square btn-sm` 相当 (アイコンのみ + Tooltip)。
 *
 * **DS の `TertiaryButton` をそのまま使う。** 以前は同じ見た目を `chakra.button` で
 * 手組みしていたが、 背景を `Surface.primary` に固定しただけで hover / press の
 * 指定が無く、 **hover しても色が変わらなかった** (2026-09-02 デザイナー指摘)。
 * design/css/components.css L215-220 の `.btn-tertiary:hover` /
 * `:active` は「色・hover・disabled は .btn 側が支配」 (同 L337-341) と明記されて
 * いるとおり DS ボタンが持つ状態なので、 手組みせず DS に任せる。
 *
 * 正方形化 (design `.btn-square` = aspect-ratio 1/1) は幅と横 padding だけで作り、
 * 高さは `size="sm"` (= 32px、 同 L311-318) に任せる。 同形の先行実装は
 * `components/common/filters/DateStepper.tsx`。
 */
type ReorderButtonProps = {
  direction: 'up' | 'down'
  label: string
  disabled: boolean
  onClick: () => void
}
const ReorderButton = ({
  direction,
  label,
  disabled,
  onClick,
}: ReorderButtonProps): React.ReactElement => {
  return (
    // disabled の button は Tooltip を取りこぼすため span で受ける必要があるが、
    // その作法は共通ラッパ ButtonTooltip に一本化されている。 ここで span を再実装しない。
    <ButtonTooltip content={label}>
      <TertiaryButton
        width="32px"
        minWidth="32px"
        aria-label={label}
        disabled={disabled}
        onClick={onClick}
        paddingX={0}
        size="sm"
        title={label}
      >
        {direction === 'up' ? (
          <FaChevronUpRegular
            boxSize={iconSize.gridInline}
            aria-hidden="true"
          />
        ) : (
          <FaChevronDownRegular
            boxSize={iconSize.gridInline}
            aria-hidden="true"
          />
        )}
      </TertiaryButton>
    </ButtonTooltip>
  )
}

export const StackOrderList = ({
  maxWidth = '480px',
  value,
  onMoveUp,
  onMoveDown,
  onResetDefault,
  onColorChange,
}: Props): React.ReactElement => {
  const { t } = useTranslation()
  const [isResetConfirmOpen, setIsResetConfirmOpen] = useState(false)

  // value が空 (= 初期値未設定) の場合はデフォルト順で表示
  const displayOrder = value.length > 0 ? value : DEFAULT_STACK_ORDER_ITEMS

  /**
   * 「今どの並びか」を同期的に保持する ref。
   *
   * 各行のボタンは描画時の index を閉じ込めるので、 **再描画が届く前に同じボタンを
   * 連打すると同じ index を送り直してしまう**。 移動後はその位置に別の種別が入って
   * いるため、 2 回目が別の種別を動かして 1 回目を打ち消す (= 並びが元に戻る)。
   * そこで押した時点で ref 側を先に入れ替え、 次の click が新しい位置を読めるようにする。
   * props が実際に更新されたら effect で同期し直す。
   */
  const orderRef = useRef<GenerationSourceTypeForSetting[]>(
    displayOrder.map((item) => item.source),
  )
  const orderKey = displayOrder.map((item) => item.source).join(',')
  useEffect(() => {
    orderRef.current = orderKey
      .split(',')
      .map((v) => Number(v) as GenerationSourceTypeForSetting)
  }, [orderKey])

  /** 押された種別の「今の位置」を ref から解決して 1 段動かす。 */
  const move = (
    source: GenerationSourceTypeForSetting,
    direction: 'up' | 'down',
  ): void => {
    const current = orderRef.current.indexOf(source)
    if (current < 0) return
    const next = direction === 'up' ? current - 1 : current + 1
    if (next < 0 || next >= orderRef.current.length) return

    const swapped = [...orderRef.current]
    swapped[current] = orderRef.current[next]
    swapped[next] = source
    orderRef.current = swapped

    if (direction === 'up') onMoveUp(current)
    else onMoveDown(current)
  }

  return (
    <Box>
      {/* 上部は案内文 + リセットのみ。 並べ替えは各行の ↑↓ で行う。 */}
      <Flex alignItems="center" gap="8px" marginBottom="8px">
        <Text as="span" color="Text.midEmphasis" fontSize="sm">
          {t('page:balanceGeneration.stackOrder.reorderHint')}
        </Text>
        <Box marginLeft="auto">
          <SecondaryButton
            aria-label={t('page:balanceGeneration.stackOrder.resetDefault')}
            onClick={() => setIsResetConfirmOpen(true)}
            size="sm"
          >
            {t('page:balanceGeneration.stackOrder.resetDefault')}
          </SecondaryButton>
        </Box>
      </Flex>
      {/* 行リスト */}
      <Flex
        flexDirection="column"
        overflow="hidden"
        maxWidth={maxWidth}
        borderWidth="1px"
        borderColor="Border.lowEmphasis"
        borderRadius="base"
        role="list"
      >
        {displayOrder.map((item, index) => {
          const isLastRow = index === displayOrder.length - 1
          const sourceName = t(SOURCE_TYPE_I18N_KEYS[item.source])
          return (
            <Flex
              key={item.source}
              alignItems="center"
              gap="12px"
              padding="8px 12px"
              borderBottomWidth={isLastRow ? '0' : '1px'}
              borderBottomColor={isLastRow ? undefined : 'Border.lowEmphasis'}
              userSelect="none"
              role="listitem"
            >
              <Text
                as="span"
                textStyle="captionStrong"
                flexShrink={0}
                width="24px"
                color="Text.midEmphasis"
                textAlign="center"
              >
                {index + 1}
              </Text>
              {/* color picker — design `.gc-stack-color` 相当
                  (= 28x24、 内部 padding 完全ゼロ、 design/css/components.css L177-179)。
                  webkit 擬似要素 (::-webkit-color-swatch-wrapper / -swatch) を sx で
                  ゼロ padding 化することで、 swatch 周りの空白を排除する。 */}
              <chakra.input
                flexShrink={0}
                aria-label={t('page:balanceGeneration.stackOrder.color.label', {
                  source: t(SOURCE_TYPE_I18N_KEYS[item.source]),
                })}
                css={{
                  width: '28px',
                  height: '24px',
                  padding: 0,
                  border: `1px solid ${LightModeSemanticColors.Border.lowEmphasis.value}`,
                  borderRadius: '4px', // BorderRadius.radiiBase
                  cursor: 'pointer',
                  background: 'transparent',
                  // chrome / safari: color swatch の inner wrapper padding を除去
                  '&::-webkit-color-swatch-wrapper': { padding: 0 },
                  '&::-webkit-color-swatch': {
                    border: 'none',
                    borderRadius: 'calc(4px - 1px)', // BorderRadius.radiiBase - 1px
                  },
                  // firefox: color swatch の inner padding を除去
                  '&::-moz-color-swatch': {
                    border: 'none',
                    borderRadius: 'calc(4px - 1px)', // BorderRadius.radiiBase - 1px
                  },
                }}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  onColorChange(item.source, e.target.value)
                }}
                type="color"
                value={
                  item.color !== ''
                    ? item.color
                    : (DEFAULT_COLORS[item.source] ?? '#cccccc')
                }
              />
              {/* flex=1 でラベルが残り幅を占め、 ↑↓ が行の右端に寄る
                  (design `.gc-stack-label` / 欠損補完設定の `.ipe-order-main` と同じ作り)。 */}
              <Text as="span" flex="1" minWidth={0} fontSize="md">
                {sourceName}
              </Text>
              {/* 行ごとの ↑↓ (design `.ecl-reorder-actions`)。
                  読み上げ名には種別名を入れる — 「上へ」だけだとどの行のボタンか分からない。 */}
              <Flex alignItems="center" flexShrink={0} gap={1}>
                <ReorderButton
                  direction="up"
                  disabled={index === 0}
                  label={t('page:balanceGeneration.stackOrder.moveUpItem', {
                    source: sourceName,
                  })}
                  onClick={() => {
                    move(item.source, 'up')
                  }}
                />
                <ReorderButton
                  direction="down"
                  disabled={isLastRow}
                  label={t('page:balanceGeneration.stackOrder.moveDownItem', {
                    source: sourceName,
                  })}
                  onClick={() => {
                    move(item.source, 'down')
                  }}
                />
              </Flex>
            </Flex>
          )
        })}
      </Flex>
      <ConfirmDialog
        body={t('page:balanceGeneration.stackOrder.resetConfirm.body')}
        confirmLabel={t('page:balanceGeneration.stackOrder.resetDefault')}
        isOpen={isResetConfirmOpen}
        onCancel={() => setIsResetConfirmOpen(false)}
        onConfirm={() => {
          setIsResetConfirmOpen(false)
          onResetDefault()
        }}
        title={t('page:balanceGeneration.stackOrder.resetConfirm.title')}
      />
    </Box>
  )
}
